/**
 * SubscriptionStepper — Industry design system step row.
 *
 * Shared 3-step progress indicator used across the public subscription
 * flow: Cadastro (signup) -> Pagamento (Stripe handoff) -> Acesso (success).
 *
 * Pure presentational component. Each step receives a state of
 * 'done' | 'active' | 'todo' that drives the square step plate per the
 * "Criar Conta" artboard grammar:
 *   - done   → accent fill + check glyph
 *   - active → accent border + 8% accent tint + accent-700 numeral
 *   - todo   → hairline border + muted numeral
 * Connectors are 1px divider lines; labels are Barlow Condensed uppercase.
 */

import { Box, Typography } from '@mui/material';

const ACC = 'var(--color-accent)';
const ACC7 = 'var(--color-accent-700)';
const TINT = 'color-mix(in srgb, var(--color-accent) 8%, transparent)';
const MUTED = 'color-mix(in srgb, var(--color-text) 58%, transparent)';
const HEADING = 'var(--font-heading)';

/**
 * Resolve the visual state for each step from the provided `activeIndex`.
 * Steps to the left of `activeIndex` are 'done', the step at the index is
 * 'active', and steps to the right are 'todo'. When `allDone` is set, every
 * step is rendered in the completed state (used on the success screen
 * after the admin account has been activated).
 */
function computeStates(steps, activeIndex, allDone) {
  return steps.map((_, i) => {
    if (allDone) return 'done';
    if (i < activeIndex) return 'done';
    if (i === activeIndex) return 'active';
    return 'todo';
  });
}

export default function SubscriptionStepper({
  activeIndex = 0,
  allDone = false,
  steps = [
    { code: '01', title: 'Cadastro', caption: 'Criação de conta' },
    { code: '02', title: 'Pagamento', caption: 'Checkout Stripe' },
    { code: '03', title: 'Acesso', caption: 'Ativação do painel' },
  ],
  sx,
}) {
  const states = computeStates(steps, activeIndex, allDone);

  return (
    <Box
      aria-label="Progresso da assinatura"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '9px',
        width: '100%',
        flexWrap: 'wrap',
        ...sx,
      }}
    >
      {steps.map((step, index) => {
        const state = states[index];
        const isLast = index === steps.length - 1;
        const activeCaption = step.activeCaption && state === 'active' ? step.activeCaption : step.caption;
        const plate = {
          done: { border: `1.5px solid ${ACC}`, background: ACC, color: 'var(--color-bg)' },
          active: { border: `1.5px solid ${ACC}`, background: TINT, color: ACC7 },
          todo: { border: '1.5px solid var(--color-divider)', background: 'transparent', color: MUTED },
        }[state];

        return (
          <Box
            key={step.code}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              minWidth: 0,
              flex: isLast ? '0 0 auto' : 1,
            }}
          >
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: 26,
                height: 24,
                flexShrink: 0,
                fontFamily: HEADING,
                fontSize: 14,
                fontWeight: 600,
                borderRadius: 0,
                ...plate,
              }}
            >
              {state === 'done' ? '✓' : String(index + 1)}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: HEADING,
                  fontSize: '12px',
                  fontWeight: 600,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: state === 'active' ? 'var(--color-text)' : MUTED,
                  whiteSpace: 'nowrap',
                  lineHeight: 1.2,
                }}
              >
                {step.title}
              </Typography>
              {activeCaption ? (
                <Typography
                  sx={{
                    fontFamily: HEADING,
                    fontSize: '11px',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: state === 'done' ? ACC7 : MUTED,
                    whiteSpace: 'nowrap',
                    lineHeight: 1.3,
                  }}
                >
                  {activeCaption}
                </Typography>
              ) : null}
            </Box>
            {!isLast && (
              <Box
                aria-hidden="true"
                sx={{
                  flex: 1,
                  height: '1px',
                  background: 'var(--color-divider)',
                  minWidth: 26,
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
}
